import { getAddress } from "viem";
import { createPublicClient, http } from "viem";
import type { PublicClient } from "viem";
import {
  CHAIN_DEFINITIONS,
  DEFAULT_CHAIN_ID,
  type SupportedChainId,
} from "./config/chains";

/* =========================================
   ✅ Token Definitions
   📝 Mainnet token addresses
========================================= */

// JPYC Token (Polygon Mainnet - EIP-3009 support)
export const JPYC_TOKEN = {
  ADDRESS: getAddress("0xE7C3D8C9a439feDe00D2600032D5dB0Be71C3c29"),
  SYMBOL: "JPYC",
  DECIMALS: 18,
  NAME: "JPY Coin",
} as const;

// NHT Token (Polygon Mainnet)
export const NHT_TOKEN = {
  ADDRESS: getAddress(
    (import.meta as any)?.env?.VITE_NHT_MAINNET_ADDRESS ||
    "0x3cc0e67d0abAbEe99F881AbADa7ef9398Dcf3757"
  ),
  SYMBOL: "NHT",
  DECIMALS: 18,
  NAME: "NHT Token",
} as const;

/* =========================================
   ✅ Gifterra コントラクト設定

   📝 現在: SBT専用コントラクト + 将来のNFT拡張準備
   🏭 将来: ファクトリーパターンでマルチプロジェクト対応
       - ファクトリーアドレス: TBD (メインネット)
       - プロジェクト別コントラクト管理
       - 導入ユーザー別オーナー権限
========================================= */

// 🎯 SBTコントラクト (本番用 - Gifterra Sol.v2)
// 環境変数でオーバーライド可能: VITE_GIFTERRA_CONTRACT_ADDRESS
export const SBT_CONTRACT = {
  ADDRESS: getAddress(
    (import.meta as any)?.env?.VITE_GIFTERRA_CONTRACT_ADDRESS ||
    "0x55f7265719ebac0d35d256f473b5455d5c83b048" // デフォルト: Gifterra Sol.v2 (Polygon Mainnet)
  ),
  TYPE: "SBT" as const,
  FEATURES: ["dailyReward", "tip", "soulbound"] as const,
} as const;

// 🆕 NFTコントラクト (譲渡可能NFT用 - 将来実装)
export const NFT_CONTRACT = {
  // ADDRESS: getAddress("0x..."), // 開発中 - NFTコントラクトデプロイ後に設定
  TYPE: "NFT" as const,
  FEATURES: ["transferable", "marketplace", "metadata"] as const,
} as const;

// 🔗 統合管理コントラクト (SBT ⟷ NFT 連携用 - 将来実装)
export const MANAGER_CONTRACT = {
  // ADDRESS: getAddress("0x..."), // 開発中 - マネージャーコントラクトデプロイ後に設定
  TYPE: "MANAGER" as const,
  FEATURES: ["sbt-nft-bridge", "level-sync", "unified-management"] as const,
} as const;

// 🏆 RankPlanRegistry コントラクト (ランクプラン管理)
export const RANK_PLAN_REGISTRY_CONTRACT = {
  // テストネット用アドレス（環境変数から取得、未設定時はundefined）
  ADDRESS: (() => {
    const raw = ((import.meta as any)?.env?.VITE_RANK_PLAN_REGISTRY_ADDRESS || '').trim();
    if (!raw) return undefined;
    try { return getAddress(raw); } catch { return undefined; }
  })(),
  TYPE: "RANK_PLAN_REGISTRY" as const,
  FEATURES: ["plan-management", "global-configuration"] as const,
} as const;

// 🔄 後方互換性のため現在のCONTRACT_ADDRESSを維持
export const CONTRACT_ADDRESS = SBT_CONTRACT.ADDRESS;

/**
 * テナント設定からGifterraコントラクトアドレスを取得
 * @returns カスタムアドレスまたはデフォルトアドレス
 */
export function getGifterraAddress(): string {
  try {
    const savedProfile = localStorage.getItem('tenant_profile');
    if (savedProfile) {
      const profile = JSON.parse(savedProfile);
      if (profile.gifterraAddress && profile.gifterraAddress !== '0x0000000000000000000000000000000000000000') {
        return profile.gifterraAddress;
      }
    }
  } catch (error) {
    console.error('Failed to load Gifterra address from tenant profile:', error);
  }
  return CONTRACT_ADDRESS; // デフォルトに戻る
}

// � メタバースEC設定
export const METAVERSE_CONFIG = {
  // 🏰 メタバース空間設定
  SPACES: {
    "world-1": {
      name: "メインワールド",
      description: "ギフテラの中心となるメタバース空間",
      isActive: true,
      machines: ["entrance-01", "vip-lounge"]
    },
    "gallery-a": {
      name: "アートギャラリー", 
      description: "クリエイター作品を展示するアート空間",
      isActive: true,
      machines: ["gallery-01", "creator-corner"]
    },
    "game-zone": {
      name: "ゲームゾーン",
      description: "ゲーム関連コンテンツの配布エリア", 
      isActive: true,
      machines: ["game-machine-01"]
    }
  },
  
  // 🏪 自販機設定
  MACHINES: {
    "entrance-01": {
      name: "エントランス自販機",
      spaceId: "world-1",
      contentSetId: "starter-pack",
      position: { x: 0, y: 0, z: 5 }
    },
    "vip-lounge": {
      name: "VIPラウンジ",
      spaceId: "world-1", 
      contentSetId: "premium-collection",
      position: { x: 10, y: 2, z: -5 }
    },
    "gallery-01": {
      name: "ギャラリー自販機",
      spaceId: "gallery-a",
      contentSetId: "art-collection",
      position: { x: -5, y: 0, z: 0 }
    },
    "creator-corner": {
      name: "クリエーターコーナー",
      spaceId: "gallery-a",
      contentSetId: "creator-pack", 
      position: { x: 5, y: 1, z: 3 }
    }
  },
  
  // 📦 コンテンツセット設定
  CONTENT_SETS: {
    "starter-pack": {
      name: "スターターパック",
      description: "初心者向けの基本コンテンツセット",
      tipThresholds: [25, 50, 100] // 段階的なアンロック
    },
    "premium-collection": {
      name: "プレミアムコレクション", 
      description: "限定的なプレミアムコンテンツ",
      tipThresholds: [200, 300, 500]
    },
    "art-collection": {
      name: "アートコレクション",
      description: "アーティスト作品のデジタルコレクション",
      tipThresholds: [100, 250]
    },
    "creator-pack": {
      name: "クリエーターパック",
      description: "クリエイター制作のオリジナルコンテンツ",
      tipThresholds: [150, 350]
    }
  }
} as const;

// �🏭 将来のファクトリー機構用設定（メインネット移行時に使用）
export const FACTORY_CONFIG = {
  // FACTORY_ADDRESS: "0x...", // メインネット展開時に設定
  // MASTER_TEMPLATE: "0x...", // マスターコントラクトテンプレート
  // METATRON_OWNER: "0x66f1274ad5d042b7571c2efa943370dbcd3459ab", // METATRON管理者
} as const;

/* =========================================
   ✅ JPYC トークン設定（GIFT HUB用）
========================================= */
export const TOKEN = {
  ADDRESS: getAddress("0xE7C3D8C9a439feDe00D2600032D5dB0Be71C3c29"), // JPYC (Polygon Mainnet)
  SYMBOL: "JPYC",
  DECIMALS: 18,
  // シンプルなSVGアイコン（モバイル対応強化）
  ICON: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTYiIGN5PSIxNiIgcj0iMTYiIGZpbGw9IiM4MjQ3ZTMiLz4KPHN2ZyB4PSI2IiB5PSI2IiB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSI+CjxwYXRoIGQ9Ik0xMiAyQzYuNDggMiAyIDYuNDggMiAxMnM0LjQ4IDEwIDEwIDEwIDEwLTQuNDggMTAtMTBTMTcuNTIgMiAxMiAyem0tMiAxNWwtNS01aDNWOGg0djRoM2wtNSA1eiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+Cjwvc3ZnPg=="
};

// Alias for backward compatibility
export const TNHT_TOKEN = TOKEN;

/* =========================================
   ✅ viem 読み取り専用クライアント
   📝 複数RPCフォールバック対応（レート制限回避）
========================================= */

// RPC優先順位: Alchemy > drpc > 公開RPC
const getRpcUrl = () => {
  // 1. 環境変数で設定されたRPC（Alchemy推奨）
  const customRpc = (import.meta as any)?.env?.VITE_ALCHEMY_RPC_URL ||
                    (import.meta as any)?.env?.VITE_POLYGON_RPC_URL;
  if (customRpc) return customRpc;

  // 2. drpc.org（無料プラン対応、レート制限緩め）
  return "https://polygon.drpc.org";
};

// チェーンごとの RPC URL 候補（先頭を優先利用）
const RPC_URLS: Record<SupportedChainId, readonly string[]> = {
  137: [
    getRpcUrl(),
    "https://polygon-rpc.com",
    "https://rpc-mainnet.matic.network",
    "https://polygon-bor-rpc.publicnode.com",
  ],
  8217: [
    (import.meta as any)?.env?.VITE_KAIA_RPC_URL || "https://public-en.node.kaia.io",
  ],
};

const publicClientCache = new Map<SupportedChainId, PublicClient>();

export function getPublicClient(
  chainId: SupportedChainId = DEFAULT_CHAIN_ID,
): PublicClient {
  let client = publicClientCache.get(chainId);
  if (!client) {
    client = createPublicClient({
      chain: CHAIN_DEFINITIONS[chainId],
      transport: http(RPC_URLS[chainId][0], {
        // タイムアウトとリトライ設定
        timeout: 30_000,
        retryCount: 3,
        retryDelay: 1000,
      }),
    }) as PublicClient;
    publicClientCache.set(chainId, client);
  }
  return client;
}

// 後方互換エイリアス
export const publicClient = getPublicClient(137);
export const polygonPublicClient = getPublicClient(137);
export const kaiaPublicClient = getPublicClient(8217);

/* =========================================
   ✅ 読み取り専用の最小 ABI（view 関数）
   ※ ここに userNFTLevel / rankThresholds を戻すのがポイント！
========================================= */
export const READ_ABI = [
  {
    inputs: [],
    name: "dailyRewardAmount",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "userInfo",
    outputs: [
      { internalType: "uint256", name: "lastClaimed", type: "uint256" },
      { internalType: "uint256", name: "totalTips", type: "uint256" },
    ],
    stateMutability: "view",
    type: "function",
  },

  // 👇 これが抜けていたために currentLevel が常に 0 になっていた
  {
    inputs: [{ internalType: "address", name: "user", type: "address" }],
    name: "userNFTLevel",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    name: "rankThresholds",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },

  {
    inputs: [],
    name: "paused",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function",
  },
] as const;

/* =========================================
   ✅ イベントABI
========================================= */
export const EVENT_ABI = [
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "user", type: "address" },
      { indexed: false, internalType: "uint256", name: "amount", type: "uint256" },
    ],
    name: "TipSent",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "user", type: "address" },
      { indexed: false, internalType: "uint256", name: "amount", type: "uint256" },
    ],
    name: "DailyRewardClaimed",
    type: "event",
  },
] as const;

/* =========================================
   ✅ フルABI（書き込み関数も含む）
========================================= */
export const CONTRACT_ABI = [
  ...READ_ABI,
  ...EVENT_ABI,
  {
    inputs: [],
    name: "owner",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "claimDailyReward",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "amount", type: "uint256" }],
    name: "tip",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [],
    name: "pause",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [],
    name: "unpause",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
] as const;

/* =========================================
   ✅ NFT コントラクト用 ABI (将来実装)
   📝 ERC721準拠 + Gifterra拡張機能
========================================= */
export const NFT_ABI = [
  // ERC721 標準関数
  {
    inputs: [{ internalType: "address", name: "owner", type: "address" }],
    name: "balanceOf",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "tokenId", type: "uint256" }],
    name: "ownerOf",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "tokenId", type: "uint256" }],
    name: "tokenURI",
    outputs: [{ internalType: "string", name: "", type: "string" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "from", type: "address" },
      { internalType: "address", name: "to", type: "address" },
      { internalType: "uint256", name: "tokenId", type: "uint256" },
    ],
    name: "transferFrom",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "to", type: "address" },
      { internalType: "uint256", name: "tokenId", type: "uint256" },
    ],
    name: "approve",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "operator", type: "address" },
      { internalType: "bool", name: "approved", type: "bool" },
    ],
    name: "setApprovalForAll",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  
  // Gifterra 拡張機能
  {
    inputs: [
      { internalType: "address", name: "to", type: "address" },
      { internalType: "uint256", name: "level", type: "uint256" },
    ],
    name: "mintLevelNFT",
    outputs: [{ internalType: "uint256", name: "tokenId", type: "uint256" }],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "tokenId", type: "uint256" }],
    name: "getTokenLevel",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
] as const;

/* =========================================
   ✅ Manager コントラクト用 ABI (将来実装)
   📝 SBT ⟷ NFT 連携管理
========================================= */
export const MANAGER_ABI = [
  {
    inputs: [{ internalType: "address", name: "user", type: "address" }],
    name: "getUserLevel",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "sbtLevel", type: "uint256" }],
    name: "convertSBTtoNFT",
    outputs: [{ internalType: "uint256", name: "tokenId", type: "uint256" }],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "tokenId", type: "uint256" }],
    name: "convertNFTtoSBT",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "user", type: "address" },
      { internalType: "uint256", name: "level", type: "uint256" },
    ],
    name: "syncLevel",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
] as const;

/* =========================================
   ✅ ERC20 最小ABI
========================================= */
export const ERC20_MIN_ABI = [
  {
    inputs: [{ internalType: "address", name: "owner", type: "address" }],
    name: "balanceOf",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "owner", type: "address" },
      { internalType: "address", name: "spender", type: "address" },
    ],
    name: "allowance",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "spender", type: "address" },
      { internalType: "uint256", name: "amount", type: "uint256" },
    ],
    name: "approve",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "to", type: "address" },
      { internalType: "uint256", name: "amount", type: "uint256" },
    ],
    name: "transfer",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [],
    name: "decimals",
    outputs: [{ internalType: "uint8", name: "", type: "uint8" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "symbol",
    outputs: [{ internalType: "string", name: "", type: "string" }],
    stateMutability: "view",
    type: "function",
  },
  // EIP-3009: transferWithAuthorization (JPYC v2)
  {
    inputs: [
      { internalType: "address", name: "from", type: "address" },
      { internalType: "address", name: "to", type: "address" },
      { internalType: "uint256", name: "value", type: "uint256" },
      { internalType: "uint256", name: "validAfter", type: "uint256" },
      { internalType: "uint256", name: "validBefore", type: "uint256" },
      { internalType: "bytes32", name: "nonce", type: "bytes32" },
      { internalType: "uint8", name: "v", type: "uint8" },
      { internalType: "bytes32", name: "r", type: "bytes32" },
      { internalType: "bytes32", name: "s", type: "bytes32" },
    ],
    name: "transferWithAuthorization",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
] as const;

/* =========================================
   ✅ PaymentSplitter ABI
   📝 GifterraPaySplitter v1.0.1 対応
   🎯 GIFT HUB購入時の収益分配に使用
========================================= */
export const PAYMENT_SPLITTER_ABI = [
  // donateERC20 - ERC20トークンでの支払い受け口
  {
    inputs: [
      { internalType: "address", name: "token", type: "address" },
      { internalType: "uint256", name: "amount", type: "uint256" },
      { internalType: "bytes32", name: "sku", type: "bytes32" },
      { internalType: "bytes32", name: "traceId", type: "bytes32" },
    ],
    name: "donateERC20",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  // donateNative - ネイティブ通貨での支払い受け口
  {
    inputs: [
      { internalType: "bytes32", name: "sku", type: "bytes32" },
      { internalType: "bytes32", name: "traceId", type: "bytes32" },
    ],
    name: "donateNative",
    outputs: [],
    stateMutability: "payable",
    type: "function",
  },
  // DonationReceived イベント
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "payer", type: "address" },
      { indexed: true, internalType: "address", name: "token", type: "address" },
      { indexed: false, internalType: "uint256", name: "amount", type: "uint256" },
      { indexed: true, internalType: "bytes32", name: "sku", type: "bytes32" },
      { indexed: false, internalType: "bytes32", name: "traceId", type: "bytes32" },
    ],
    name: "DonationReceived",
    type: "event",
  },
  // releaseAllERC20 - 全受益者へのERC20分配
  {
    inputs: [
      { internalType: "contract IERC20", name: "token", type: "address" },
    ],
    name: "releaseAllERC20",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  // releaseAll - 全受益者へのネイティブ通貨分配
  {
    inputs: [],
    name: "releaseAll",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  // owner - コントラクトオーナー取得
  {
    inputs: [],
    name: "owner",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function",
  },
] as const;

/* =========================================
   ✅ PaymentSplitter V2 ABI（即時分配型）
   📝 GifterraPaySplitterV2 - 非カストディ版
   🎯 受領と同時に各payeeへ即座に分配
========================================= */
export const PAYMENT_SPLITTER_V2_ABI = [
  // コンストラクタ
  {
    inputs: [
      { internalType: "address[]", name: "payees", type: "address[]" },
      { internalType: "uint256[]", name: "shares", type: "uint256[]" },
    ],
    stateMutability: "nonpayable",
    type: "constructor",
  },
  // distributeERC20 - ERC20トークンの即時分配
  {
    inputs: [
      { internalType: "address", name: "token", type: "address" },
      { internalType: "uint256", name: "amount", type: "uint256" },
      { internalType: "bytes32", name: "sku", type: "bytes32" },
      { internalType: "bytes32", name: "traceId", type: "bytes32" },
    ],
    name: "distributeERC20",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  // transferERC20Direct - 単一宛先への直接送金（分配設定なしの場合用）
  {
    inputs: [
      { internalType: "address", name: "token", type: "address" },
      { internalType: "uint256", name: "amount", type: "uint256" },
      { internalType: "address", name: "recipient", type: "address" },
      { internalType: "bytes32", name: "sku", type: "bytes32" },
      { internalType: "bytes32", name: "traceId", type: "bytes32" },
    ],
    name: "transferERC20Direct",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  // distributeNative - ネイティブ通貨の即時分配
  {
    inputs: [
      { internalType: "bytes32", name: "sku", type: "bytes32" },
      { internalType: "bytes32", name: "traceId", type: "bytes32" },
    ],
    name: "distributeNative",
    outputs: [],
    stateMutability: "payable",
    type: "function",
  },
  // ImmediateDistribution イベント
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "payer", type: "address" },
      { indexed: true, internalType: "address", name: "token", type: "address" },
      { indexed: false, internalType: "uint256", name: "amount", type: "uint256" },
      { indexed: true, internalType: "bytes32", name: "sku", type: "bytes32" },
      { indexed: false, internalType: "bytes32", name: "traceId", type: "bytes32" },
    ],
    name: "ImmediateDistribution",
    type: "event",
  },
  // PaymentDistributed イベント
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "payee", type: "address" },
      { indexed: true, internalType: "address", name: "token", type: "address" },
      { indexed: false, internalType: "uint256", name: "amount", type: "uint256" },
    ],
    name: "PaymentDistributed",
    type: "event",
  },
  // Payee管理
  {
    inputs: [
      { internalType: "address", name: "wallet", type: "address" },
      { internalType: "uint256", name: "shares_", type: "uint256" },
    ],
    name: "addPayee",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [{ internalType: "address", name: "wallet", type: "address" }],
    name: "removePayee",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "wallet", type: "address" },
      { internalType: "uint256", name: "newShares", type: "uint256" },
    ],
    name: "updateShares",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  // View関数
  {
    inputs: [],
    name: "payeeCount",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "totalShares",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "index", type: "uint256" }],
    name: "getPayee",
    outputs: [
      { internalType: "address", name: "wallet", type: "address" },
      { internalType: "uint256", name: "shares_", type: "uint256" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "getStats",
    outputs: [
      { internalType: "uint256", name: "payeeCount_", type: "uint256" },
      { internalType: "uint256", name: "totalShares_", type: "uint256" },
      { internalType: "uint256", name: "totalDistributedNative_", type: "uint256" },
      { internalType: "bool", name: "isPaused", type: "bool" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "address", name: "token", type: "address" }],
    name: "getTotalDistributedERC20",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "owner",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "version",
    outputs: [{ internalType: "string", name: "", type: "string" }],
    stateMutability: "pure",
    type: "function",
  },
] as const;

/* =========================================
   ✅ GifterraFactoryLite_v2 コントラクトアドレス
   📝 総合KODOMI対応版ファクトリー（Polygon Mainnet）
   🎯 Gifterra_v2をデプロイ
========================================= */
export const GIFTERRA_FACTORY_LITE_V2_ADDRESS = getAddress("0x8B6F466E82A7472d29034B5B0B7CACCB86Eae66B");

/* =========================================
   ✅ GifterraFactoryLite_v3 コントラクトアドレス
   📝 同ランク再ミント対応版ファクトリー（Polygon Mainnet）
   🎯 Gifterra_v3をデプロイ
   📅 デプロイ日時: 2026-01-02
   ✨ 新機能: JPYC/NHTトークンがデフォルトで設定済み
========================================= */
export const GIFTERRA_FACTORY_LITE_V3_ADDRESS = getAddress(
  (import.meta as any)?.env?.VITE_GIFTERRA_FACTORY_V3_ADDRESS ||
  "0xe4DE30770051c5F33BE0122bf3D3767AB523C18E" // デフォルト: v3ファクトリー (Polygon Mainnet)
);

// RankPlanRegistry アドレス（Polygon Mainnet）
export const RANK_PLAN_REGISTRY_ADDRESS = getAddress("0xE74b3952F6E7E7abA2A4b26197b5609fE04E1eB8");

// 後方互換性のためのエイリアス（非推奨）
export const GIFTERRA_FACTORY_ADDRESS = GIFTERRA_FACTORY_LITE_V2_ADDRESS;

/* =========================================
   ✅ GifterraFactoryLite_v2 ABI
   📝 総合KODOMI対応版ファクトリー（Gifterra_v2をデプロイ）
========================================= */
export const GIFTERRA_FACTORY_ABI = [
  // createTenant - 新規テナント作成（Gifterra_v2をデプロイ）
  {
    inputs: [
      { internalType: "string", name: "tenantName", type: "string" },
      { internalType: "address", name: "admin", type: "address" },
      { internalType: "address", name: "rewardTokenAddress", type: "address" },
      { internalType: "address", name: "tipWalletAddress", type: "address" },
      { internalType: "uint8", name: "rankPlan", type: "uint8" },
    ],
    name: "createTenant",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "payable",
    type: "function",
  },
  // deploymentFee - デプロイ手数料
  {
    inputs: [],
    name: "deploymentFee",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // getTenantIdByAdmin - 管理者からテナントID取得
  {
    inputs: [{ internalType: "address", name: "admin", type: "address" }],
    name: "adminToTenantId",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // nextTenantId - 次のテナントID
  {
    inputs: [],
    name: "nextTenantId",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // totalTenants - 総テナント数
  {
    inputs: [],
    name: "totalTenants",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // activeTenants - アクティブテナント数
  {
    inputs: [],
    name: "activeTenants",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // TenantCreated イベント（Lite_v2版 - Gifterra_v2のみ）
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "tenantId", type: "uint256" },
      { indexed: true, internalType: "address", name: "admin", type: "address" },
      { indexed: false, internalType: "string", name: "tenantName", type: "string" },
      { indexed: false, internalType: "address", name: "gifterra", type: "address" },
    ],
    name: "TenantCreated",
    type: "event",
  },
  // resetAdminTenant - 管理者のテナント紐付けをリセット（再デプロイ用）
  {
    inputs: [{ internalType: "address", name: "admin", type: "address" }],
    name: "resetAdminTenant",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
] as const;

// 後方互換性のためのエイリアス
export const GIFTERRA_FACTORY_LITE_ABI = GIFTERRA_FACTORY_ABI;

/* =========================================
   ✅ RewardNFT_v2 バイトコード取得用
   📝 スーパーアドミンが直接デプロイするために使用
========================================= */
export const REWARD_NFT_V2_CONSTRUCTOR_ABI = [
  {
    inputs: [
      { internalType: "string", name: "name", type: "string" },
      { internalType: "string", name: "symbol", type: "string" },
      { internalType: "string", name: "baseURI", type: "string" },
      { internalType: "address", name: "owner", type: "address" },
      { internalType: "address", name: "_distributorAddress", type: "address" },
      { internalType: "uint256", name: "_maxSupply", type: "uint256" },
      { internalType: "uint256", name: "_mintPrice", type: "uint256" },
    ],
    stateMutability: "nonpayable",
    type: "constructor",
  },
] as const;

/* =========================================
   ✅ FlagNFT ABI（SBT/NFTミント用）
   📝 カテゴリ付きNFT - isTransferable=falseでSBT化
========================================= */
export const FLAG_NFT_ABI = [
  // コンストラクタ
  {
    inputs: [
      { internalType: "string", name: "name", type: "string" },
      { internalType: "string", name: "symbol", type: "string" },
      { internalType: "string", name: "baseURI", type: "string" },
      { internalType: "address", name: "owner", type: "address" },
      { internalType: "uint256", name: "maxSupply", type: "uint256" },
    ],
    stateMutability: "nonpayable",
    type: "constructor",
  },
  // バージョン関数
  {
    inputs: [],
    name: "version",
    outputs: [{ internalType: "string", name: "", type: "string" }],
    stateMutability: "pure",
    type: "function",
  },
  // ロール管理関数
  {
    inputs: [
      { internalType: "bytes32", name: "role", type: "bytes32" },
      { internalType: "address", name: "account", type: "address" },
    ],
    name: "grantRole",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "bytes32", name: "role", type: "bytes32" },
      { internalType: "address", name: "account", type: "address" },
    ],
    name: "hasRole",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "MINTER_ROLE",
    outputs: [{ internalType: "bytes32", name: "", type: "bytes32" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "DEFAULT_ADMIN_ROLE",
    outputs: [{ internalType: "bytes32", name: "", type: "bytes32" }],
    stateMutability: "view",
    type: "function",
  },
  // バーン関数（NFTを焼却）
  {
    inputs: [{ internalType: "uint256", name: "tokenId", type: "uint256" }],
    name: "burn",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  // パブリックミント関数（ユーザーがガス代を払ってミント）
  {
    inputs: [{ internalType: "uint8", name: "category", type: "uint8" }],
    name: "publicMintWithCategory",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "nonpayable",
    type: "function",
  },
  // パブリックミント有効フラグ
  {
    inputs: [{ internalType: "uint8", name: "category", type: "uint8" }],
    name: "categoryPublicMintEnabled",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function",
  },
  // パブリックミント設定（管理者用）
  {
    inputs: [
      { internalType: "uint8", name: "category", type: "uint8" },
      { internalType: "bool", name: "enabled", type: "bool" },
    ],
    name: "setCategoryPublicMint",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  // バッチカテゴリ初期化（v1.2.0）
  {
    inputs: [
      { internalType: "uint8[]", name: "categories", type: "uint8[]" },
      { internalType: "uint8", name: "usageLimit", type: "uint8" },
      { internalType: "bool", name: "isTransferable", type: "bool" },
      { internalType: "string", name: "metadataURI", type: "string" },
    ],
    name: "configureCategoriesBatch",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  // バッチパブリックミント設定（v1.2.0）
  {
    inputs: [
      { internalType: "uint8[]", name: "categories", type: "uint8[]" },
      { internalType: "bool", name: "enabled", type: "bool" },
    ],
    name: "setCategoryPublicMintBatch",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  // ミント関数（管理者/ミンター用）
  {
    inputs: [
      { internalType: "address", name: "to", type: "address" },
      { internalType: "uint8", name: "category", type: "uint8" },
    ],
    name: "mintWithCategory",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "nonpayable",
    type: "function",
  },
  // バッチミント
  {
    inputs: [
      { internalType: "address[]", name: "recipients", type: "address[]" },
      { internalType: "uint8", name: "category", type: "uint8" },
    ],
    name: "mintBatchWithCategory",
    outputs: [{ internalType: "uint256[]", name: "tokenIds", type: "uint256[]" }],
    stateMutability: "nonpayable",
    type: "function",
  },
  // カテゴリ設定
  {
    inputs: [
      { internalType: "uint8", name: "category", type: "uint8" },
      { internalType: "uint8", name: "usageLimit", type: "uint8" },
      { internalType: "uint64", name: "validFrom", type: "uint64" },
      { internalType: "uint64", name: "validUntil", type: "uint64" },
      { internalType: "bool", name: "isTransferable", type: "bool" },
      { internalType: "string", name: "metadataURI", type: "string" },
    ],
    name: "configureCategory",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  // 読み取り関数
  {
    inputs: [{ internalType: "uint256", name: "tokenId", type: "uint256" }],
    name: "ownerOf",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "address", name: "owner", type: "address" }],
    name: "balanceOf",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "totalSupply",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint8", name: "category", type: "uint8" }],
    name: "totalSupplyByCategory",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "tokenId", type: "uint256" }],
    name: "getCategoryOf",
    outputs: [{ internalType: "uint8", name: "", type: "uint8" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint8", name: "category", type: "uint8" }],
    name: "categoryConfig",
    outputs: [
      { internalType: "uint8", name: "category", type: "uint8" },
      { internalType: "uint8", name: "usageLimit", type: "uint8" },
      { internalType: "uint64", name: "validFrom", type: "uint64" },
      { internalType: "uint64", name: "validUntil", type: "uint64" },
      { internalType: "bool", name: "isTransferable", type: "bool" },
      { internalType: "string", name: "metadataURI", type: "string" },
    ],
    stateMutability: "view",
    type: "function",
  },
  // 管理関数
  {
    inputs: [{ internalType: "string", name: "newBaseURI", type: "string" }],
    name: "setBaseURI",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "tokenId", type: "uint256" }],
    name: "tokenURI",
    outputs: [{ internalType: "string", name: "", type: "string" }],
    stateMutability: "view",
    type: "function",
  },
  // baseURI読み取り関数（ERC721拡張）
  {
    inputs: [],
    name: "baseURI",
    outputs: [{ internalType: "string", name: "", type: "string" }],
    stateMutability: "view",
    type: "function",
  },
  // 自動バーン付きパブリックミント（v2.0.0）
  {
    inputs: [{ internalType: "uint8", name: "category", type: "uint8" }],
    name: "mintWithAutoBurn",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "nonpayable",
    type: "function",
  },
  // カテゴリ発行上限設定
  {
    inputs: [
      { internalType: "uint8", name: "category", type: "uint8" },
      { internalType: "uint256", name: "_maxSupply", type: "uint256" },
    ],
    name: "setCategoryMaxSupply",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  // 全体発行上限設定
  {
    inputs: [{ internalType: "uint256", name: "newMaxSupply", type: "uint256" }],
    name: "setMaxSupply",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  // maxSupply読み取り
  {
    inputs: [],
    name: "maxSupply",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // カテゴリ発行上限読み取り
  {
    inputs: [{ internalType: "uint8", name: "", type: "uint8" }],
    name: "categoryMaxSupply",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // カテゴリ発行数読み取り
  {
    inputs: [{ internalType: "uint8", name: "", type: "uint8" }],
    name: "categoryMintedCount",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // ユーザー×カテゴリ別トークンID
  {
    inputs: [
      { internalType: "address", name: "", type: "address" },
      { internalType: "uint8", name: "", type: "uint8" },
    ],
    name: "userCategoryToken",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // NFT使用状況
  {
    inputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    name: "nftUsage",
    outputs: [
      { internalType: "uint8", name: "usedCount", type: "uint8" },
      { internalType: "uint64", name: "lastUsedAt", type: "uint64" },
    ],
    stateMutability: "view",
    type: "function",
  },
  // ========================================
  // v2.3.0: シリーズ管理関数
  // ========================================
  {
    inputs: [
      { internalType: "uint8", name: "category", type: "uint8" },
      { internalType: "string", name: "name", type: "string" },
    ],
    name: "createSeries",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "seriesId", type: "uint256" },
      { internalType: "uint256", name: "_maxSupply", type: "uint256" },
    ],
    name: "setSeriesMaxSupply",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "seriesId", type: "uint256" },
      { internalType: "bool", name: "enabled", type: "bool" },
    ],
    name: "setSeriesPublicMint",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  // v2.3.0: シリーズ付きミント
  {
    inputs: [
      { internalType: "address", name: "to", type: "address" },
      { internalType: "uint8", name: "category", type: "uint8" },
      { internalType: "uint256", name: "seriesId", type: "uint256" },
    ],
    name: "mintWithSeries",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint8", name: "category", type: "uint8" },
      { internalType: "uint256", name: "seriesId", type: "uint256" },
    ],
    name: "mintWithSeriesAutoBurn",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "nonpayable",
    type: "function",
  },
  // v2.3.0: Per-tokenメタデータ・譲渡制御ミント
  {
    inputs: [
      { internalType: "address", name: "to", type: "address" },
      { internalType: "uint8", name: "category", type: "uint8" },
      { internalType: "string", name: "metadataURI", type: "string" },
    ],
    name: "mintWithMetadata",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "to", type: "address" },
      { internalType: "uint8", name: "category", type: "uint8" },
      { internalType: "string", name: "metadataURI", type: "string" },
      { internalType: "bool", name: "isTransferable", type: "bool" },
    ],
    name: "mintWithMetadataAndTransferability",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "nonpayable",
    type: "function",
  },
  // v2.3.0: Per-tokenオーバーライド設定
  {
    inputs: [
      { internalType: "uint256", name: "tokenId", type: "uint256" },
      { internalType: "string", name: "metadataURI", type: "string" },
    ],
    name: "setTokenMetadataURI",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "tokenId", type: "uint256" },
      { internalType: "bool", name: "isTransferable", type: "bool" },
    ],
    name: "setTokenTransferable",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "tokenId", type: "uint256" }],
    name: "clearTokenTransferableOverride",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  // v2.3.0: シリーズ View関数
  {
    inputs: [{ internalType: "uint256", name: "tokenId", type: "uint256" }],
    name: "getSeriesOf",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "user", type: "address" },
      { internalType: "uint256", name: "seriesId", type: "uint256" },
    ],
    name: "getUserSeriesToken",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "seriesId", type: "uint256" }],
    name: "getSeriesConfig",
    outputs: [
      {
        components: [
          { internalType: "uint8", name: "category", type: "uint8" },
          { internalType: "string", name: "name", type: "string" },
          { internalType: "uint256", name: "seriesId", type: "uint256" },
          { internalType: "bool", name: "isActive", type: "bool" },
        ],
        internalType: "struct FlagNFT.SeriesConfig",
        name: "",
        type: "tuple",
      },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "seriesId", type: "uint256" }],
    name: "totalSupplyBySeries",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // v2.3.0: シリーズ public mapping getters
  {
    inputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    name: "seriesPublicMintEnabled",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    name: "tokenSeriesId",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "", type: "address" },
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    name: "userSeriesToken",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    name: "seriesMintedCount",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    name: "seriesMaxSupply",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // v2.3.0: Per-token View関数
  {
    inputs: [{ internalType: "uint256", name: "tokenId", type: "uint256" }],
    name: "getTokenMetadataURI",
    outputs: [{ internalType: "string", name: "", type: "string" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "tokenId", type: "uint256" }],
    name: "hasTransferableOverride",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "tokenId", type: "uint256" }],
    name: "isTokenTransferable",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function",
  },
  // イベント
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "to", type: "address" },
      { indexed: true, internalType: "uint256", name: "tokenId", type: "uint256" },
      { indexed: true, internalType: "uint8", name: "category", type: "uint8" },
    ],
    name: "FlagNFTMinted",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "tokenId", type: "uint256" },
      { indexed: true, internalType: "address", name: "owner", type: "address" },
      { indexed: true, internalType: "uint8", name: "category", type: "uint8" },
    ],
    name: "FlagNFTBurned",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "to", type: "address" },
      { indexed: true, internalType: "uint256", name: "newTokenId", type: "uint256" },
      { indexed: true, internalType: "uint256", name: "burnedTokenId", type: "uint256" },
      { indexed: false, internalType: "uint8", name: "category", type: "uint8" },
    ],
    name: "FlagNFTMintedWithAutoBurn",
    type: "event",
  },
  // v2.3.0: シリーズイベント
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "seriesId", type: "uint256" },
      { indexed: true, internalType: "uint8", name: "category", type: "uint8" },
      { indexed: false, internalType: "string", name: "name", type: "string" },
    ],
    name: "SeriesCreated",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "seriesId", type: "uint256" },
      { indexed: false, internalType: "uint256", name: "maxSupply", type: "uint256" },
    ],
    name: "SeriesMaxSupplySet",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "seriesId", type: "uint256" },
      { indexed: false, internalType: "bool", name: "enabled", type: "bool" },
    ],
    name: "SeriesPublicMintSet",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "to", type: "address" },
      { indexed: true, internalType: "uint256", name: "tokenId", type: "uint256" },
      { indexed: true, internalType: "uint256", name: "seriesId", type: "uint256" },
      { indexed: false, internalType: "uint8", name: "category", type: "uint8" },
    ],
    name: "FlagNFTMintedWithSeries",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "to", type: "address" },
      { indexed: true, internalType: "uint256", name: "newTokenId", type: "uint256" },
      { indexed: true, internalType: "uint256", name: "burnedTokenId", type: "uint256" },
      { indexed: false, internalType: "uint256", name: "seriesId", type: "uint256" },
      { indexed: false, internalType: "uint8", name: "category", type: "uint8" },
    ],
    name: "FlagNFTMintedWithSeriesAutoBurn",
    type: "event",
  },
  // v2.3.0: Per-tokenイベント
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "tokenId", type: "uint256" },
      { indexed: false, internalType: "string", name: "metadataURI", type: "string" },
    ],
    name: "TokenMetadataURISet",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "tokenId", type: "uint256" },
      { indexed: false, internalType: "bool", name: "isTransferable", type: "bool" },
    ],
    name: "TokenTransferableSet",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "tokenId", type: "uint256" },
    ],
    name: "TokenTransferableOverrideCleared",
    type: "event",
  },
] as const;

/* =========================================
   ✅ FlagNFT バイトコード取得用
========================================= */
export const FLAG_NFT_CONSTRUCTOR_ABI = [
  {
    inputs: [
      { internalType: "string", name: "name", type: "string" },
      { internalType: "string", name: "symbol", type: "string" },
      { internalType: "string", name: "baseURI", type: "string" },
      { internalType: "address", name: "owner", type: "address" },
      { internalType: "uint256", name: "_maxSupply", type: "uint256" },
    ],
    stateMutability: "nonpayable",
    type: "constructor",
  },
] as const;

/* =========================================
   ✅ GifterraPaySplitter バイトコード取得用
========================================= */
export const GIFTERRA_PAY_SPLITTER_CONSTRUCTOR_ABI = [
  {
    inputs: [
      { internalType: "address[]", name: "payees_", type: "address[]" },
      { internalType: "uint256[]", name: "shares_", type: "uint256[]" },
    ],
    stateMutability: "nonpayable",
    type: "constructor",
  },
] as const;

/* =========================================
   ✅ Gifterra_v2 ABI (総合KODOMI対応版)
   📝 JPYC + 応援熱量を追跡してSBT自動配布
========================================= */
export const GIFTERRA_V2_ABI = [
  // 投げ銭関数
  {
    inputs: [{ internalType: "uint256", name: "amount", type: "uint256" }],
    name: "tipJPYC",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "amount", type: "uint256" }],
    name: "tipSupport",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "amount", type: "uint256" }],
    name: "tip",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  // デイリーリワード
  {
    inputs: [],
    name: "claimDailyReward",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  // 読み取り関数
  {
    inputs: [{ internalType: "address", name: "user", type: "address" }],
    name: "userInfo",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
      { internalType: "uint256", name: "", type: "uint256" },
      { internalType: "uint256", name: "", type: "uint256" },
      { internalType: "uint256", name: "", type: "uint256" },
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "jpycTips",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "supportTips",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "totalKodomi",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "userNFTLevel",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    name: "rankThresholds",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "getAllRankThresholds",
    outputs: [{ internalType: "uint256[]", name: "", type: "uint256[]" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "getAllRankNames",
    outputs: [{ internalType: "string[]", name: "", type: "string[]" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "maxRankLevel",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "version",
    outputs: [{ internalType: "string", name: "", type: "string" }],
    stateMutability: "pure",
    type: "function",
  },
  // 管理者ミント関数（スーパーアドミン用）
  {
    inputs: [
      { internalType: "address", name: "to", type: "address" },
      { internalType: "uint256", name: "level", type: "uint256" },
    ],
    name: "adminMint",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "nonpayable",
    type: "function",
  },
  // リワード関連（v2.1.0 - ネイティブPOL対応）
  {
    inputs: [],
    name: "dailyRewardAmount",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "rewardToken",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "useNativeReward",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "rewardPoolBalance",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "_amount", type: "uint256" }],
    name: "setDailyRewardAmount",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [{ internalType: "address", name: "_addr", type: "address" }],
    name: "setRewardToken",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [{ internalType: "bool", name: "_useNative", type: "bool" }],
    name: "setUseNativeReward",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "lastClaimed",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // RewardPoolChargedイベント
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "from", type: "address" },
      { indexed: false, internalType: "uint256", name: "amount", type: "uint256" },
    ],
    name: "RewardPoolCharged",
    type: "event",
  },
  // RewardModeChangedイベント
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: "bool", name: "useNative", type: "bool" },
    ],
    name: "RewardModeChanged",
    type: "event",
  },
  // DailyRewardClaimedイベント
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "user", type: "address" },
      { indexed: false, internalType: "uint256", name: "amount", type: "uint256" },
    ],
    name: "DailyRewardClaimed",
    type: "event",
  },
  // イベント
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "from", type: "address" },
      { indexed: false, internalType: "uint256", name: "amount", type: "uint256" },
      { indexed: false, internalType: "uint256", name: "newKodomi", type: "uint256" },
    ],
    name: "TippedJPYC",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "from", type: "address" },
      { indexed: false, internalType: "uint256", name: "amount", type: "uint256" },
      { indexed: false, internalType: "uint256", name: "newKodomi", type: "uint256" },
    ],
    name: "TippedSupport",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "user", type: "address" },
      { indexed: false, internalType: "uint256", name: "jpyc", type: "uint256" },
      { indexed: false, internalType: "uint256", name: "support", type: "uint256" },
      { indexed: false, internalType: "uint256", name: "total", type: "uint256" },
    ],
    name: "KodomiUpdated",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "user", type: "address" },
      { indexed: false, internalType: "uint256", name: "tokenId", type: "uint256" },
      { indexed: false, internalType: "uint256", name: "level", type: "uint256" },
    ],
    name: "NFTMinted",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "user", type: "address" },
      { indexed: false, internalType: "uint256", name: "tokenId", type: "uint256" },
    ],
    name: "NFTBurned",
    type: "event",
  },
  // パブリックミント関連（v2.1.0 - ユーザーがガス代を払ってミント）
  {
    inputs: [],
    name: "publicMintEnabled",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "bool", name: "_enabled", type: "bool" }],
    name: "setPublicMintEnabled",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "kodomiAmount", type: "uint256" }],
    name: "publicMint",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "level", type: "uint256" }],
    name: "publicMintByLevel",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "user", type: "address" },
      { internalType: "uint256", name: "kodomiAmount", type: "uint256" },
    ],
    name: "canPublicMint",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "user", type: "address" },
      { internalType: "uint256", name: "kodomiAmount", type: "uint256" },
    ],
    name: "getNextMintableLevel",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // PublicMintEnabledイベント
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: "bool", name: "enabled", type: "bool" },
    ],
    name: "PublicMintEnabled",
    type: "event",
  },
  // PublicMintExecutedイベント
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "user", type: "address" },
      { indexed: false, internalType: "uint256", name: "tokenId", type: "uint256" },
      { indexed: false, internalType: "uint256", name: "level", type: "uint256" },
      { indexed: false, internalType: "uint256", name: "kodomi", type: "uint256" },
    ],
    name: "PublicMintExecuted",
    type: "event",
  },
  // 管理関数
  {
    inputs: [{ internalType: "string", name: "newBaseURI", type: "string" }],
    name: "setBaseURI",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "tokenId", type: "uint256" }],
    name: "tokenURI",
    outputs: [{ internalType: "string", name: "", type: "string" }],
    stateMutability: "view",
    type: "function",
  },
] as const;

/* =========================================
   ✅ GifterraFactoryLite_v3 ABI
   📝 同ランク再ミント対応版ファクトリー（Gifterra_v3をデプロイ）
========================================= */
export const GIFTERRA_FACTORY_V3_ABI = [
  // createTenant - 新規テナント作成（Gifterra_v3をデプロイ）
  {
    inputs: [
      { internalType: "string", name: "tenantName", type: "string" },
      { internalType: "address", name: "admin", type: "address" },
      { internalType: "address", name: "rewardTokenAddress", type: "address" },
      { internalType: "address", name: "tipWalletAddress", type: "address" },
      { internalType: "uint8", name: "rankPlan", type: "uint8" },
    ],
    name: "createTenant",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "payable",
    type: "function",
  },
  // createTenantWithRemintOption - 同ランク再ミント設定指定版
  {
    inputs: [
      { internalType: "string", name: "tenantName", type: "string" },
      { internalType: "address", name: "admin", type: "address" },
      { internalType: "address", name: "rewardTokenAddress", type: "address" },
      { internalType: "address", name: "tipWalletAddress", type: "address" },
      { internalType: "uint8", name: "rankPlan", type: "uint8" },
      { internalType: "bool", name: "allowSameLevelRemint", type: "bool" },
    ],
    name: "createTenantWithRemintOption",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "payable",
    type: "function",
  },
  // deploymentFee - デプロイ手数料
  {
    inputs: [],
    name: "deploymentFee",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // defaultAllowSameLevelRemint - デフォルトの同ランク再ミント設定
  {
    inputs: [],
    name: "defaultAllowSameLevelRemint",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function",
  },
  // getTenantIdByAdmin - 管理者からテナントID取得
  {
    inputs: [{ internalType: "address", name: "admin", type: "address" }],
    name: "adminToTenantId",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // nextTenantId - 次のテナントID
  {
    inputs: [],
    name: "nextTenantId",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // totalTenants - 総テナント数
  {
    inputs: [],
    name: "totalTenants",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // activeTenants - アクティブテナント数
  {
    inputs: [],
    name: "activeTenants",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // TenantCreated イベント（v3版 - allowSameLevelRemintを含む）
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "tenantId", type: "uint256" },
      { indexed: true, internalType: "address", name: "admin", type: "address" },
      { indexed: false, internalType: "string", name: "tenantName", type: "string" },
      { indexed: false, internalType: "address", name: "gifterra", type: "address" },
      { indexed: false, internalType: "bool", name: "allowSameLevelRemint", type: "bool" },
    ],
    name: "TenantCreated",
    type: "event",
  },
  // resetAdminTenant - 管理者のテナント紐付けをリセット
  {
    inputs: [{ internalType: "address", name: "admin", type: "address" }],
    name: "resetAdminTenant",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  // version
  {
    inputs: [],
    name: "version",
    outputs: [{ internalType: "string", name: "", type: "string" }],
    stateMutability: "pure",
    type: "function",
  },
] as const;

/* =========================================
   ✅ Gifterra_v3 ABI (同ランク再ミント対応版)
   📝 allowSameLevelRemint でフレキシブルモード切替可能
========================================= */
export const GIFTERRA_V3_ABI = [
  // 投げ銭関数
  {
    inputs: [{ internalType: "uint256", name: "amount", type: "uint256" }],
    name: "tipJPYC",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "amount", type: "uint256" }],
    name: "tipSupport",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "amount", type: "uint256" }],
    name: "tip",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  // デイリーリワード
  {
    inputs: [],
    name: "claimDailyReward",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  // 読み取り関数
  {
    inputs: [{ internalType: "address", name: "user", type: "address" }],
    name: "userInfo",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
      { internalType: "uint256", name: "", type: "uint256" },
      { internalType: "uint256", name: "", type: "uint256" },
      { internalType: "uint256", name: "", type: "uint256" },
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "jpycTips",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "supportTips",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "totalKodomi",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "userNFTLevel",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    name: "rankThresholds",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "getAllRankThresholds",
    outputs: [{ internalType: "uint256[]", name: "", type: "uint256[]" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "getAllRankNames",
    outputs: [{ internalType: "string[]", name: "", type: "string[]" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "maxRankLevel",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "version",
    outputs: [{ internalType: "string", name: "", type: "string" }],
    stateMutability: "pure",
    type: "function",
  },
  // リワード関連
  {
    inputs: [],
    name: "dailyRewardAmount",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "rewardToken",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "useNativeReward",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "rewardPoolBalance",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "address", name: "", type: "address" }],
    name: "lastClaimed",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // パブリックミント関連
  {
    inputs: [],
    name: "publicMintEnabled",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "bool", name: "_enabled", type: "bool" }],
    name: "setPublicMintEnabled",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "kodomiAmount", type: "uint256" }],
    name: "publicMint",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [{ internalType: "uint256", name: "level", type: "uint256" }],
    name: "publicMintByLevel",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "user", type: "address" },
      { internalType: "uint256", name: "kodomiAmount", type: "uint256" },
    ],
    name: "canPublicMint",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "user", type: "address" },
      { internalType: "uint256", name: "kodomiAmount", type: "uint256" },
    ],
    name: "getNextMintableLevel",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // 管理者ミント関数（スーパーアドミン用）
  {
    inputs: [
      { internalType: "address", name: "to", type: "address" },
      { internalType: "uint256", name: "level", type: "uint256" },
    ],
    name: "adminMint",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "nonpayable",
    type: "function",
  },
  // v3追加: 同ランク再ミント関連
  {
    inputs: [],
    name: "allowSameLevelRemint",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "bool", name: "_allowed", type: "bool" }],
    name: "setAllowSameLevelRemint",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "user", type: "address" },
      { internalType: "uint256", name: "level", type: "uint256" },
    ],
    name: "getUserMintCount",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  // イベント
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "user", type: "address" },
      { indexed: false, internalType: "uint256", name: "tokenId", type: "uint256" },
      { indexed: false, internalType: "uint256", name: "level", type: "uint256" },
    ],
    name: "NFTMinted",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "user", type: "address" },
      { indexed: false, internalType: "uint256", name: "tokenId", type: "uint256" },
    ],
    name: "NFTBurned",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: "bool", name: "enabled", type: "bool" },
    ],
    name: "PublicMintEnabled",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "user", type: "address" },
      { indexed: false, internalType: "uint256", name: "tokenId", type: "uint256" },
      { indexed: false, internalType: "uint256", name: "level", type: "uint256" },
      { indexed: false, internalType: "uint256", name: "kodomi", type: "uint256" },
    ],
    name: "PublicMintExecuted",
    type: "event",
  },
  // v3追加: 同ランク再ミントイベント
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "user", type: "address" },
      { indexed: false, internalType: "uint256", name: "oldTokenId", type: "uint256" },
      { indexed: false, internalType: "uint256", name: "newTokenId", type: "uint256" },
      { indexed: false, internalType: "uint256", name: "level", type: "uint256" },
      { indexed: false, internalType: "uint256", name: "mintCount", type: "uint256" },
    ],
    name: "SameLevelRemint",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: "bool", name: "allowed", type: "bool" },
    ],
    name: "SameLevelRemintModeChanged",
    type: "event",
  },
] as const;

/* =========================================
   ✅ FlagNFT_ERC1155 ABI (ERC1155版) v1.1.0
   📝 Spatialトークンゲート対応版
   🎯 シンプルToken ID方式: Token ID = Category enum値（0〜5）
========================================= */
export const FLAG_NFT_ERC1155_ABI = [
  // Constructor
  {
    inputs: [
      { internalType: "string", name: "_name", type: "string" },
      { internalType: "string", name: "_symbol", type: "string" },
      { internalType: "string", name: "baseURI", type: "string" },
      { internalType: "address", name: "owner", type: "address" },
    ],
    stateMutability: "nonpayable",
    type: "constructor",
  },
  // Functions
  {
    inputs: [
      { internalType: "address", name: "account", type: "address" },
      { internalType: "uint256", name: "id", type: "uint256" },
    ],
    name: "balanceOf",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address[]", name: "accounts", type: "address[]" },
      { internalType: "uint256[]", name: "ids", type: "uint256[]" },
    ],
    name: "balanceOfBatch",
    outputs: [
      { internalType: "uint256[]", name: "", type: "uint256[]" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "tokenId", type: "uint256" },
      { internalType: "uint256", name: "amount", type: "uint256" },
    ],
    name: "burn",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "account", type: "address" },
      { internalType: "uint256", name: "tokenId", type: "uint256" },
      { internalType: "uint256", name: "amount", type: "uint256" },
    ],
    name: "burnFrom",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "enum FlagNFT_ERC1155.Category", name: "", type: "uint8" },
    ],
    name: "categoryInitialized",
    outputs: [
      { internalType: "bool", name: "", type: "bool" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "enum FlagNFT_ERC1155.Category", name: "", type: "uint8" },
    ],
    name: "categoryPublicMintEnabled",
    outputs: [
      { internalType: "bool", name: "", type: "bool" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "tokenId", type: "uint256" },
    ],
    name: "clearTokenTransferableOverride",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
      { internalType: "uint8", name: "usageLimit", type: "uint8" },
      { internalType: "uint64", name: "validFrom", type: "uint64" },
      { internalType: "uint64", name: "validUntil", type: "uint64" },
      { internalType: "bool", name: "isTransferable", type: "bool" },
      { internalType: "uint256", name: "maxSupply", type: "uint256" },
      { internalType: "string", name: "metadataURI", type: "string" },
    ],
    name: "configureCategory",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
      { internalType: "string", name: "seriesName", type: "string" },
    ],
    name: "createSeries",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [],
    name: "DEFAULT_ADMIN_ROLE",
    outputs: [
      { internalType: "bytes32", name: "", type: "bytes32" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "deleteDefaultRoyalty",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [],
    name: "FLAG_SETTER_ROLE",
    outputs: [
      { internalType: "bytes32", name: "", type: "bytes32" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "user", type: "address" },
      { internalType: "uint256", name: "tokenId", type: "uint256" },
    ],
    name: "flagsOf",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "tokenId", type: "uint256" },
    ],
    name: "getCategoryOf",
    outputs: [
      { internalType: "enum FlagNFT_ERC1155.Category", name: "", type: "uint8" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "bytes32", name: "role", type: "bytes32" },
    ],
    name: "getRoleAdmin",
    outputs: [
      { internalType: "bytes32", name: "", type: "bytes32" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "seriesId", type: "uint256" },
    ],
    name: "getSeriesConfig",
    outputs: [
      { internalType: "struct FlagNFT_ERC1155.SeriesConfig", name: "", type: "tuple", components: [{ internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" }, { internalType: "string", name: "name", type: "string" }, { internalType: "uint256", name: "seriesId", type: "uint256" }, { internalType: "uint256", name: "tokenId", type: "uint256" }, { internalType: "bool", name: "isActive", type: "bool" }] },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "seriesId", type: "uint256" },
    ],
    name: "getSeriesOf",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
    ],
    name: "getTokenIdForCategory",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "pure",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "tokenId", type: "uint256" },
    ],
    name: "getTokenMetadataURI",
    outputs: [
      { internalType: "string", name: "", type: "string" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "user", type: "address" },
      { internalType: "uint256", name: "seriesId", type: "uint256" },
    ],
    name: "getUserSeriesToken",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "bytes32", name: "role", type: "bytes32" },
      { internalType: "address", name: "account", type: "address" },
    ],
    name: "grantRole",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "user", type: "address" },
      { internalType: "uint256", name: "tokenId", type: "uint256" },
      { internalType: "uint8", name: "bit", type: "uint8" },
    ],
    name: "hasFlag",
    outputs: [
      { internalType: "bool", name: "", type: "bool" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "bytes32", name: "role", type: "bytes32" },
      { internalType: "address", name: "account", type: "address" },
    ],
    name: "hasRole",
    outputs: [
      { internalType: "bool", name: "", type: "bool" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "tokenId", type: "uint256" },
    ],
    name: "hasTransferableOverride",
    outputs: [
      { internalType: "bool", name: "", type: "bool" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "bool", name: "enablePublicMint", type: "bool" },
    ],
    name: "initializeAllCategories",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
      { internalType: "bool", name: "enablePublicMint", type: "bool" },
    ],
    name: "initializeCategory",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "account", type: "address" },
      { internalType: "address", name: "operator", type: "address" },
    ],
    name: "isApprovedForAll",
    outputs: [
      { internalType: "bool", name: "", type: "bool" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
    ],
    name: "isCategoryConfigured",
    outputs: [
      { internalType: "bool", name: "", type: "bool" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "tokenId", type: "uint256" },
    ],
    name: "isTokenTransferable",
    outputs: [
      { internalType: "bool", name: "", type: "bool" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "to", type: "address" },
      { internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
      { internalType: "uint256", name: "amount", type: "uint256" },
    ],
    name: "mint",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address[]", name: "recipients", type: "address[]" },
      { internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
      { internalType: "uint256", name: "amountEach", type: "uint256" },
    ],
    name: "mintBatch",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [],
    name: "MINTER_ROLE",
    outputs: [
      { internalType: "bytes32", name: "", type: "bytes32" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
    ],
    name: "mintWithAutoBurn",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "to", type: "address" },
      { internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
      { internalType: "string", name: "metadataURI", type: "string" },
    ],
    name: "mintWithMetadata",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "to", type: "address" },
      { internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
      { internalType: "string", name: "metadataURI", type: "string" },
      { internalType: "bool", name: "isTransferable", type: "bool" },
    ],
    name: "mintWithMetadataAndTransferability",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "to", type: "address" },
      { internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
      { internalType: "uint256", name: "seriesId", type: "uint256" },
    ],
    name: "mintWithSeries",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
      { internalType: "uint256", name: "seriesId", type: "uint256" },
    ],
    name: "mintWithSeriesAutoBurn",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [],
    name: "name",
    outputs: [
      { internalType: "string", name: "", type: "string" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "", type: "address" },
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    name: "nftUsage",
    outputs: [
      { internalType: "uint8", name: "usedCount", type: "uint8" },
      { internalType: "uint64", name: "lastUsedAt", type: "uint64" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "pause",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [],
    name: "paused",
    outputs: [
      { internalType: "bool", name: "", type: "bool" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "PAUSER_ROLE",
    outputs: [
      { internalType: "bytes32", name: "", type: "bytes32" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
    ],
    name: "publicMint",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "bytes32", name: "role", type: "bytes32" },
      { internalType: "address", name: "account", type: "address" },
    ],
    name: "renounceRole",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "tokenId", type: "uint256" },
    ],
    name: "resetTokenRoyalty",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "bytes32", name: "role", type: "bytes32" },
      { internalType: "address", name: "account", type: "address" },
    ],
    name: "revokeRole",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "tokenId", type: "uint256" },
      { internalType: "uint256", name: "salePrice", type: "uint256" },
    ],
    name: "royaltyInfo",
    outputs: [
      { internalType: "address", name: "", type: "address" },
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "from", type: "address" },
      { internalType: "address", name: "to", type: "address" },
      { internalType: "uint256[]", name: "ids", type: "uint256[]" },
      { internalType: "uint256[]", name: "amounts", type: "uint256[]" },
      { internalType: "bytes", name: "data", type: "bytes" },
    ],
    name: "safeBatchTransferFrom",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "from", type: "address" },
      { internalType: "address", name: "to", type: "address" },
      { internalType: "uint256", name: "id", type: "uint256" },
      { internalType: "uint256", name: "amount", type: "uint256" },
      { internalType: "bytes", name: "data", type: "bytes" },
    ],
    name: "safeTransferFrom",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    name: "seriesConfig",
    outputs: [
      { internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
      { internalType: "string", name: "name", type: "string" },
      { internalType: "uint256", name: "seriesId", type: "uint256" },
      { internalType: "uint256", name: "tokenId", type: "uint256" },
      { internalType: "bool", name: "isActive", type: "bool" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    name: "seriesMaxSupply",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    name: "seriesMintedCount",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    name: "seriesPublicMintEnabled",
    outputs: [
      { internalType: "bool", name: "", type: "bool" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "operator", type: "address" },
      { internalType: "bool", name: "approved", type: "bool" },
    ],
    name: "setApprovalForAll",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
      { internalType: "bool", name: "enabled", type: "bool" },
    ],
    name: "setCategoryPublicMint",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "enum FlagNFT_ERC1155.Category[]", name: "categories", type: "uint8[]" },
      { internalType: "bool", name: "enabled", type: "bool" },
    ],
    name: "setCategoryPublicMintBatch",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "receiver", type: "address" },
      { internalType: "uint96", name: "feeNumerator", type: "uint96" },
    ],
    name: "setDefaultRoyalty",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "user", type: "address" },
      { internalType: "uint256", name: "tokenId", type: "uint256" },
      { internalType: "uint8", name: "bit", type: "uint8" },
      { internalType: "bool", name: "value", type: "bool" },
      { internalType: "bytes32", name: "traceId", type: "bytes32" },
    ],
    name: "setFlag",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "user", type: "address" },
      { internalType: "uint256", name: "tokenId", type: "uint256" },
      { internalType: "uint256", name: "setMask", type: "uint256" },
      { internalType: "uint256", name: "clearMask", type: "uint256" },
      { internalType: "bytes32", name: "traceId", type: "bytes32" },
    ],
    name: "setFlagsByMask",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "seriesId", type: "uint256" },
      { internalType: "uint256", name: "_maxSupply", type: "uint256" },
    ],
    name: "setSeriesMaxSupply",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "seriesId", type: "uint256" },
      { internalType: "bool", name: "enabled", type: "bool" },
    ],
    name: "setSeriesPublicMint",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "tokenId", type: "uint256" },
      { internalType: "string", name: "metadataURI", type: "string" },
    ],
    name: "setTokenMetadataURI",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "tokenId", type: "uint256" },
      { internalType: "address", name: "receiver", type: "address" },
      { internalType: "uint96", name: "feeNumerator", type: "uint96" },
    ],
    name: "setTokenRoyalty",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "tokenId", type: "uint256" },
      { internalType: "bool", name: "isTransferable", type: "bool" },
    ],
    name: "setTokenTransferable",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "string", name: "newURI", type: "string" },
    ],
    name: "setURI",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "bytes4", name: "interfaceId", type: "bytes4" },
    ],
    name: "supportsInterface",
    outputs: [
      { internalType: "bool", name: "", type: "bool" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "symbol",
    outputs: [
      { internalType: "string", name: "", type: "string" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    name: "tokenConfig",
    outputs: [
      { internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
      { internalType: "uint8", name: "usageLimit", type: "uint8" },
      { internalType: "uint64", name: "validFrom", type: "uint64" },
      { internalType: "uint64", name: "validUntil", type: "uint64" },
      { internalType: "bool", name: "isTransferable", type: "bool" },
      { internalType: "uint256", name: "maxSupply", type: "uint256" },
      { internalType: "string", name: "metadataURI", type: "string" },
      { internalType: "bool", name: "exists", type: "bool" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    name: "tokenMintedCount",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "tokenId", type: "uint256" },
    ],
    name: "totalSupply",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "seriesId", type: "uint256" },
    ],
    name: "totalSupplyBySeries",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "unpause",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
      { internalType: "uint8", name: "usageLimit", type: "uint8" },
      { internalType: "uint64", name: "validFrom", type: "uint64" },
      { internalType: "uint64", name: "validUntil", type: "uint64" },
      { internalType: "bool", name: "isTransferable", type: "bool" },
      { internalType: "uint256", name: "maxSupply", type: "uint256" },
      { internalType: "string", name: "metadataURI", type: "string" },
    ],
    name: "updateCategory",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "tokenId", type: "uint256" },
    ],
    name: "uri",
    outputs: [
      { internalType: "string", name: "", type: "string" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "user", type: "address" },
      { internalType: "uint256", name: "tokenId", type: "uint256" },
    ],
    name: "useNFT",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "", type: "address" },
      { internalType: "enum FlagNFT_ERC1155.Category", name: "", type: "uint8" },
    ],
    name: "userCategoryToken",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [
      { internalType: "address", name: "", type: "address" },
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    name: "userSeriesToken",
    outputs: [
      { internalType: "uint256", name: "", type: "uint256" },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "version",
    outputs: [
      { internalType: "string", name: "", type: "string" },
    ],
    stateMutability: "pure",
    type: "function",
  },
  // Events
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "account", type: "address" },
      { indexed: true, internalType: "address", name: "operator", type: "address" },
      { indexed: false, internalType: "bool", name: "approved", type: "bool" },
    ],
    name: "ApprovalForAll",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
      { indexed: false, internalType: "bool", name: "publicMintEnabled", type: "bool" },
    ],
    name: "CategoryInitialized",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
      { indexed: false, internalType: "bool", name: "enabled", type: "bool" },
    ],
    name: "CategoryPublicMintSet",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "tokenId", type: "uint256" },
      { indexed: true, internalType: "address", name: "owner", type: "address" },
      { indexed: true, internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
      { indexed: false, internalType: "uint256", name: "amount", type: "uint256" },
    ],
    name: "FlagNFTBurned",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "to", type: "address" },
      { indexed: true, internalType: "uint256", name: "tokenId", type: "uint256" },
      { indexed: true, internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
      { indexed: false, internalType: "uint256", name: "amount", type: "uint256" },
    ],
    name: "FlagNFTMinted",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "to", type: "address" },
      { indexed: true, internalType: "uint256", name: "tokenId", type: "uint256" },
      { indexed: true, internalType: "uint256", name: "seriesId", type: "uint256" },
      { indexed: false, internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
    ],
    name: "FlagNFTMintedWithSeries",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "to", type: "address" },
      { indexed: true, internalType: "uint256", name: "newTokenId", type: "uint256" },
      { indexed: true, internalType: "uint256", name: "burnedTokenId", type: "uint256" },
      { indexed: false, internalType: "uint256", name: "seriesId", type: "uint256" },
      { indexed: false, internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
    ],
    name: "FlagNFTMintedWithSeriesAutoBurn",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "tokenId", type: "uint256" },
      { indexed: true, internalType: "address", name: "user", type: "address" },
      { indexed: false, internalType: "uint8", name: "newUsedCount", type: "uint8" },
      { indexed: false, internalType: "uint64", name: "usedAt", type: "uint64" },
    ],
    name: "FlagNFTUsed",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "tokenId", type: "uint256" },
      { indexed: true, internalType: "address", name: "user", type: "address" },
      { indexed: false, internalType: "uint256", name: "setMask", type: "uint256" },
      { indexed: false, internalType: "uint256", name: "clearMask", type: "uint256" },
      { indexed: false, internalType: "address", name: "operator", type: "address" },
      { indexed: false, internalType: "bytes32", name: "traceId", type: "bytes32" },
    ],
    name: "FlagsBatchUpdated",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "tokenId", type: "uint256" },
      { indexed: true, internalType: "address", name: "user", type: "address" },
      { indexed: true, internalType: "uint8", name: "bit", type: "uint8" },
      { indexed: false, internalType: "bool", name: "value", type: "bool" },
      { indexed: false, internalType: "address", name: "operator", type: "address" },
      { indexed: false, internalType: "bytes32", name: "traceId", type: "bytes32" },
    ],
    name: "FlagUpdated",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: "address", name: "account", type: "address" },
    ],
    name: "Paused",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "bytes32", name: "role", type: "bytes32" },
      { indexed: true, internalType: "bytes32", name: "previousAdminRole", type: "bytes32" },
      { indexed: true, internalType: "bytes32", name: "newAdminRole", type: "bytes32" },
    ],
    name: "RoleAdminChanged",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "bytes32", name: "role", type: "bytes32" },
      { indexed: true, internalType: "address", name: "account", type: "address" },
      { indexed: true, internalType: "address", name: "sender", type: "address" },
    ],
    name: "RoleGranted",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "bytes32", name: "role", type: "bytes32" },
      { indexed: true, internalType: "address", name: "account", type: "address" },
      { indexed: true, internalType: "address", name: "sender", type: "address" },
    ],
    name: "RoleRevoked",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "receiver", type: "address" },
      { indexed: false, internalType: "uint96", name: "feeNumerator", type: "uint96" },
    ],
    name: "RoyaltyInfoUpdated",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "seriesId", type: "uint256" },
      { indexed: true, internalType: "uint256", name: "tokenId", type: "uint256" },
      { indexed: true, internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
      { indexed: false, internalType: "string", name: "name", type: "string" },
    ],
    name: "SeriesCreated",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "seriesId", type: "uint256" },
      { indexed: false, internalType: "uint256", name: "maxSupply", type: "uint256" },
    ],
    name: "SeriesMaxSupplySet",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "seriesId", type: "uint256" },
      { indexed: false, internalType: "bool", name: "enabled", type: "bool" },
    ],
    name: "SeriesPublicMintSet",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "tokenId", type: "uint256" },
      { indexed: true, internalType: "enum FlagNFT_ERC1155.Category", name: "category", type: "uint8" },
      { indexed: false, internalType: "uint8", name: "usageLimit", type: "uint8" },
      { indexed: false, internalType: "uint64", name: "validFrom", type: "uint64" },
      { indexed: false, internalType: "uint64", name: "validUntil", type: "uint64" },
      { indexed: false, internalType: "bool", name: "isTransferable", type: "bool" },
      { indexed: false, internalType: "uint256", name: "maxSupply", type: "uint256" },
      { indexed: false, internalType: "string", name: "metadataURI", type: "string" },
    ],
    name: "TokenConfigured",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "tokenId", type: "uint256" },
      { indexed: false, internalType: "string", name: "metadataURI", type: "string" },
    ],
    name: "TokenMetadataURISet",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "tokenId", type: "uint256" },
      { indexed: true, internalType: "address", name: "receiver", type: "address" },
      { indexed: false, internalType: "uint96", name: "feeNumerator", type: "uint96" },
    ],
    name: "TokenRoyaltyUpdated",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "tokenId", type: "uint256" },
    ],
    name: "TokenTransferableOverrideCleared",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "tokenId", type: "uint256" },
      { indexed: false, internalType: "bool", name: "isTransferable", type: "bool" },
    ],
    name: "TokenTransferableSet",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "operator", type: "address" },
      { indexed: true, internalType: "address", name: "from", type: "address" },
      { indexed: true, internalType: "address", name: "to", type: "address" },
      { indexed: false, internalType: "uint256[]", name: "ids", type: "uint256[]" },
      { indexed: false, internalType: "uint256[]", name: "values", type: "uint256[]" },
    ],
    name: "TransferBatch",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "address", name: "operator", type: "address" },
      { indexed: true, internalType: "address", name: "from", type: "address" },
      { indexed: true, internalType: "address", name: "to", type: "address" },
      { indexed: false, internalType: "uint256", name: "id", type: "uint256" },
      { indexed: false, internalType: "uint256", name: "value", type: "uint256" },
    ],
    name: "TransferSingle",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: "address", name: "account", type: "address" },
    ],
    name: "Unpaused",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: "string", name: "value", type: "string" },
      { indexed: true, internalType: "uint256", name: "id", type: "uint256" },
    ],
    name: "URI",
    type: "event",
  },
  // Errors
  {
    inputs: [],
    name: "BatchTooLarge",
    type: "error",
  },
  {
    inputs: [],
    name: "BelowCurrentSupply",
    type: "error",
  },
  {
    inputs: [],
    name: "CategoryNotConfigured",
    type: "error",
  },
  {
    inputs: [],
    name: "CategoryNotInitialized",
    type: "error",
  },
  {
    inputs: [],
    name: "EmptyArray",
    type: "error",
  },
  {
    inputs: [],
    name: "EmptyBatch",
    type: "error",
  },
  {
    inputs: [],
    name: "ExceedsMaxSupply",
    type: "error",
  },
  {
    inputs: [],
    name: "Expired",
    type: "error",
  },
  {
    inputs: [],
    name: "InsufficientBalance",
    type: "error",
  },
  {
    inputs: [],
    name: "InvalidRoyaltyReceiver",
    type: "error",
  },
  {
    inputs: [],
    name: "InvalidValidRange",
    type: "error",
  },
  {
    inputs: [],
    name: "MasksOverlap",
    type: "error",
  },
  {
    inputs: [],
    name: "MaxSupplyReached",
    type: "error",
  },
  {
    inputs: [],
    name: "NotOwnerOrMinter",
    type: "error",
  },
  {
    inputs: [],
    name: "NotTransferable",
    type: "error",
  },
  {
    inputs: [],
    name: "NotYetValid",
    type: "error",
  },
  {
    inputs: [],
    name: "PublicMintDisabled",
    type: "error",
  },
  {
    inputs: [],
    name: "RoyaltyTooHigh",
    type: "error",
  },
  {
    inputs: [],
    name: "SeriesCategoryMismatch",
    type: "error",
  },
  {
    inputs: [],
    name: "SeriesNameRequired",
    type: "error",
  },
  {
    inputs: [],
    name: "SeriesNotActive",
    type: "error",
  },
  {
    inputs: [],
    name: "SeriesNotFound",
    type: "error",
  },
  {
    inputs: [],
    name: "SeriesSupplyReached",
    type: "error",
  },
  {
    inputs: [],
    name: "TokenNotConfigured",
    type: "error",
  },
  {
    inputs: [],
    name: "TooManyCategories",
    type: "error",
  },
  {
    inputs: [],
    name: "UsageLimitExceeded",
    type: "error",
  },
  {
    inputs: [],
    name: "ZeroAddress",
    type: "error",
  },
] as const;

/* =========================================
   ✅ FlagNFT_ERC1155 コンストラクタABI
========================================= */
export const FLAG_NFT_ERC1155_CONSTRUCTOR_ABI = [
  {
    inputs: [
      { internalType: "string", name: "_name", type: "string" },
      { internalType: "string", name: "_symbol", type: "string" },
      { internalType: "string", name: "baseURI", type: "string" },
      { internalType: "address", name: "owner", type: "address" },
    ],
    stateMutability: "nonpayable",
    type: "constructor",
  },
] as const;